import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { IAppConfig } from '@models/appconfig';
import { IWALResponse } from '@models/product-detail-report/average-life';
import { IProductRateForecast } from '@models/product-detail-report/product-rate-forecast';
import { IVintageRunOffResponse, IVintageRunOffSchedule, VintageRunOffType } from '@models/product-detail-report/vintage-run-off';
import { getDateString } from '@shared/dates';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ProductDetailService {
  private readonly VINTAGE_RUN_OFF_END_POINT = '/api/vintagerunoff';
  private readonly PRODUCT_RATE_FORECAST = '/api/productrateforecasts';
  private environment: IAppConfig;

  private readonly vintageRunoffs = {};

  constructor(private readonly http: HttpClient, private readonly appConfig: AppConfigProvider) {
    this.appConfig.environment.subscribe(env => {
      this.environment = env;
    });
  }

  public sortByPeriodEndDate(vintageRunOffTimeSeries) {
    return vintageRunOffTimeSeries.sort((a1, a2) => new Date(a1.PeriodEndDate).getTime() - new Date(a2.PeriodEndDate).getTime());
  }

  public getVintageRunOffBalances(productId: number): Observable<IVintageRunOffResponse> {
    if (productId) {
      return this.http
        .get<IVintageRunOffResponse>(`${this.environment.API.REPORTS}${this.VINTAGE_RUN_OFF_END_POINT}/${productId}`)
        .pipe(map(vintageRunOffResponse => this.parseVintageRunOffResponse(vintageRunOffResponse, productId)));
    }
  }

  public parseVintageRunOffResponse(vintageRunOffResponse: IVintageRunOffResponse, productId: number = null): IVintageRunOffResponse {
    // Ensuring the run offs are sorted by date
    if (vintageRunOffResponse.VintageRunOffs) {
      vintageRunOffResponse.VintageRunOffs.forEach(vintageRunOffs => this.sortByPeriodEndDate(vintageRunOffs.VintageRunOffTimeSeries));
    }

    // Ensuring the run off schedule is sorted by date
    vintageRunOffResponse.VintageRunOffSchedule.forEach(vintageRunOffSchedule =>
      this.sortByPeriodEndDate(vintageRunOffSchedule.VintageRunOffScheduleItems)
    );

    // Sorting by run off type to ensure that the active is selected first
    vintageRunOffResponse.VintageRunOffSchedule.sort((a1, a2) => a1.RunOffType - a2.RunOffType);

    vintageRunOffResponse.VintageRunOffs.forEach(runOff => {
      runOff.VintageRunOffTimeSeries.pop();
    });

    if (productId !== null) {
      // Caching request
      this.vintageRunoffs[productId] = vintageRunOffResponse;
    }

    return vintageRunOffResponse;
  }

  public getProductRateForecasts(productId: number): Observable<IProductRateForecast[]> {
    // Cache removed, request is small and must be refetched on any Beta calculation
    return this.http.get<IProductRateForecast[]>(`${this.environment.API.REFERENCE_DATA}${this.PRODUCT_RATE_FORECAST}/${productId}`).pipe(
      map((forecasts: IProductRateForecast[]) => {
        forecasts.forEach((forecast: IProductRateForecast) => {
          forecast.PeriodEndDate = getDateString(forecast.PeriodEndDate);
        });

        return forecasts;
      })
    );
  }

  public _calculateVintageRunoff(productId: number) {
    try {
      this.http.get(`${this.environment.API.REPORTS}${this.VINTAGE_RUN_OFF_END_POINT}/calculate/${productId}`).toPromise();
    } catch (err) {
      console.warn(`CalculateVintageRunoff failed. NYI`, err);
    }
  }

  public getVintageRunOffSchedule(
    productId: number,
    asOfDate: Date,
    vintageRunOffType: VintageRunOffType
  ): Observable<IVintageRunOffSchedule> {
    return this.http.get<IVintageRunOffSchedule>(
      `${this.environment.API.REPORTS}/api/vintagerunoff/${productId}/${asOfDate.toJSON().substr(0, 10)}/${vintageRunOffType}/schedule`
    );
  }

  public getWALForProducts(productIdList: number[], asOfDate: Date) {
    const body = { productIdList, asOfDate };

    return this.http.post<IWALResponse[]>(`${this.environment.API.REPORTS}/api/vintagerunoff/wal`, body);
  }
}
